import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { Button, Alert } from "antd";
import PropertyAutoComplete from "./PropertyAutoComplete";
import CommunityMultiSelect from "./CommunityMultiSelect";
import SpellCheckTextArea from "./SpellCheckTextArea";
import useSpellCheck from "../useSpellCheck";
import styles from "../CreateSMS.module.scss";

const messageLockedHint = "Please select a Community before typing your message.";

const CreateSMSStep1 = ({
  properties,
  searchText,
  selectedProperty,
  selectedCommunities,
  smsText,
  maxMessageLength,
  wasSubmitAttempted,
  hasNoAvailableCommunities,
  onPropertyChange,
  onSelectProperty,
  onCommunitiesChange,
  onSmsTextChange,
  onCancel,
  onNext,
}) => {
  const isOverLimit = smsText.length > maxMessageLength;
  const sortedProperties = [...properties].sort((a, b) =>
    a.PropertyName.localeCompare(b.PropertyName, undefined, { sensitivity: "base" })
  );

  const isMessageLocked =
    hasNoAvailableCommunities || !selectedProperty || selectedCommunities.length === 0;

  const [showLockedTooltip, setShowLockedTooltip] = useState(false);
  const hideTooltipTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isMessageLocked) setShowLockedTooltip(false);
  }, [isMessageLocked]);

  useEffect(() => {
    return () => clearTimeout(hideTooltipTimeoutRef.current);
  }, []);

  const handleLockedMessageClick = () => {
    if (!isMessageLocked) return;
    setShowLockedTooltip(true);
    clearTimeout(hideTooltipTimeoutRef.current);
    hideTooltipTimeoutRef.current = setTimeout(() => setShowLockedTooltip(false), 2000);
  };

  const sortedCommunities = (selectedProperty?.Communities ?? [])
    .slice()
    .sort((a, b) => a.CommunityName.localeCompare(b.CommunityName));

  const spellRanges = useSpellCheck(smsText, !isMessageLocked && smsText.trim().length > 0);

  return (
    <>
      <div className={styles.formRow}>
        <div className={styles.field}>
          <PropertyAutoComplete
            data-testid="sms-property-select"
            className={styles.select}
            properties={sortedProperties}
            value={searchText}
            onChange={onPropertyChange}
            onSelect={onSelectProperty}
            disabled={hasNoAvailableCommunities}
            status={
              wasSubmitAttempted && !selectedProperty && !hasNoAvailableCommunities
                ? "error"
                : undefined
            }
          />
          {wasSubmitAttempted && !selectedProperty && !hasNoAvailableCommunities && (
            <div className={styles.errorMsg}>Please select a property to continue.</div>
          )}
        </div>

        <div className={styles.field}>
          <CommunityMultiSelect
            data-testid="sms-community-select"
            className={styles.select}
            communities={sortedCommunities}
            value={selectedCommunities}
            onChange={onCommunitiesChange}
            disabled={!selectedProperty || hasNoAvailableCommunities}
            status={
              wasSubmitAttempted && selectedCommunities.length === 0 && !hasNoAvailableCommunities
                ? "error"
                : undefined
            }
          />
          {wasSubmitAttempted && selectedCommunities.length === 0 && !hasNoAvailableCommunities && (
            <div className={styles.errorMsg}>Please select at least one community to continue.</div>
          )}
        </div>
      </div>

      <div className={styles.infoWrapper}>
        <Alert
          type={hasNoAvailableCommunities ? "warning" : "info"}
          showIcon
          message={
            hasNoAvailableCommunities
              ? "You are not currently linked to any property with an active contract. Please contact your administrator to get access."
              : "Only recipients who have opted in to receive SMS communications will be included."
          }
          className={styles.customAlert}
        />
      </div>

      <div>
        <div style={{ position: "relative" }}>
          <SpellCheckTextArea
            id="sms-message-input"
            rows={6}
            placeholder="Type your SMS message here..."
            className={`${styles.textArea} ${styles.textAreaWrapper}`}
            value={smsText}
            onChange={onSmsTextChange}
            ranges={spellRanges}
            maxLength={maxMessageLength}
            lang="en-US"
            disabled={isMessageLocked}
            status={
              wasSubmitAttempted && smsText.trim().length === 0 && !hasNoAvailableCommunities
                ? "error"
                : undefined
            }
          />
          {isMessageLocked && (
            <div
              onClick={handleLockedMessageClick}
              style={{ position: "absolute", inset: 0, cursor: "not-allowed" }}
            />
          )}
        </div>
        {showLockedTooltip && (
          <div className={styles.errorMsg}>{messageLockedHint}</div>
        )}
        <div style={{ textAlign: "right", fontSize: 12, color: "#374151", marginTop: 4 }}>
          {smsText.length} / {maxMessageLength}
        </div>
        {wasSubmitAttempted && smsText.trim().length === 0 && !hasNoAvailableCommunities && (
          <div className={styles.errorMsg}>Please enter a message to continue.</div>
        )}
        {isOverLimit && (
          <div className={styles.errorMsg}>
            Message is too long. Please shorten it.
          </div>
        )}
      </div>

      <div className={styles.buttons}>
        <Button data-testid="sms-cancel-btn" onClick={onCancel} disabled={hasNoAvailableCommunities}>
          Cancel
        </Button>
        <Button
          data-testid="sms-next-btn"
          type="primary"
          onClick={onNext}
          disabled={hasNoAvailableCommunities || isOverLimit}
        >
          Next
        </Button>
      </div>
    </>
  );
};

CreateSMSStep1.propTypes = {
  properties: PropTypes.arrayOf(PropTypes.object).isRequired,
  searchText: PropTypes.string.isRequired,
  selectedProperty: PropTypes.object,
  selectedCommunities: PropTypes.arrayOf(PropTypes.string).isRequired,
  smsText: PropTypes.string.isRequired,
  maxMessageLength: PropTypes.number.isRequired,
  wasSubmitAttempted: PropTypes.bool.isRequired,
  hasNoAvailableCommunities: PropTypes.bool.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onSelectProperty: PropTypes.func.isRequired,
  onCommunitiesChange: PropTypes.func.isRequired,
  onSmsTextChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default CreateSMSStep1;
